import { useAuth, useSessionExpiration } from "@/features/auth";
import { FeatureFlagProvider } from "@/features/config";
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
    Container,
    LoadingIndicator,
    SidebarInset,
    SidebarProvider,
} from "@/shared/components";
import { DossierTraceProvider } from "@/shared/components/utils/dossier-trace-provider.tsx";
import { Fragment, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router";
import { Navbar } from "./navbar.tsx";

const breadcrumbMap: Record<string, string> = {
    admin: "Admin",
    users: "Users",
    scripts: "Scripts",
    scenarios: "Scenarios",
    settings: "Settings",
    profile: "Profile",
    "release-highlights": "Release highlights",
};

export default function RootLayout() {
    const location = useLocation();
    const navigate = useNavigate();
    const { isAuthenticated, isLoading } = useAuth();
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const isAdminPath = pathSegments[0] === "admin";
    const isLoginPath = location.pathname === "/login";

    // Initialize session expiration handling
    useSessionExpiration();

    useEffect(() => {
        if (isLoginPath && isAuthenticated && !isLoading) {
            void navigate("/", { replace: true });
        }
    }, [isLoginPath, isAuthenticated, isLoading, navigate]);

    if (isLoading) {
        return (
            <div className="flex min-h-full flex-1 flex-col">
                <LoadingIndicator fullHeight size="medium" message="Loading application..." className="flex-1" />
            </div>
        );
    }

    return (
        <DossierTraceProvider>
            <FeatureFlagProvider>
                <SidebarProvider defaultOpen={true}>
                    <SidebarInset>
                        <div className="flex min-h-full flex-1 flex-col" style={{ "--header-height": "64px" } as React.CSSProperties}>
                            <Navbar />

                            {isAdminPath && (
                                <Container className="my-4">
                                    <Breadcrumb>
                                        <BreadcrumbList>
                                            <BreadcrumbItem>
                                                <BreadcrumbLink href="/">Home</BreadcrumbLink>
                                            </BreadcrumbItem>
                                            {pathSegments.map((segment: string, index: number) => (
                                                <Fragment key={segment}>
                                                    <BreadcrumbSeparator />
                                                    <BreadcrumbItem>
                                                        {index === pathSegments.length - 1 ? (
                                                            <BreadcrumbPage>{breadcrumbMap[segment] || segment}</BreadcrumbPage>
                                                        ) : (
                                                            <BreadcrumbLink href={`/${pathSegments.slice(0, index + 1).join("/")}`}>
                                                                {breadcrumbMap[segment] || segment}
                                                            </BreadcrumbLink>
                                                        )}
                                                    </BreadcrumbItem>
                                                </Fragment>
                                            ))}
                                        </BreadcrumbList>
                                    </Breadcrumb>
                                </Container>
                            )}

                            <main className="flex flex-1">
                                <Outlet />
                            </main>
                        </div>
                    </SidebarInset>
                </SidebarProvider>
            </FeatureFlagProvider>
        </DossierTraceProvider>
    );
}
